/**
 * Multi-Factor Authentication Service
 * Uses Firebase Auth's native MFA functionality
 */

import {
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  MultiFactorResolver,
  getMultiFactorResolver,
  getAuth,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";

const db = getFirestore();
const auth = getAuth();
import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  collection,
  query,
  getDocs,
} from "firebase/firestore";

export interface MFASystemSettings {
  enabled: boolean;
  required: boolean;
  gracePeriodDays: number;
  enabledBy: string;
  enabledAt: Date;
  lastModifiedBy: string;
  lastModifiedAt: Date;
}

export interface MFAUserSettings {
  userId: string;
  hasTotp: boolean;
  totpEnrolledAt?: Date;
  lastUsed?: Date;
  backupCodes: string[];
  isRequired: boolean;
}

export class MFAService {
  private static instance: MFAService;

  public static getInstance(): MFAService {
    if (!MFAService.instance) {
      MFAService.instance = new MFAService();
    }
    return MFAService.instance;
  }

  /**
   * Check if MFA is enabled system-wide
   */
  async isMFAEnabled(): Promise<boolean> {
    try {
      const settingsDoc = await getDoc(doc(db, "system_settings", "mfa"));
      if (!settingsDoc.exists()) {
        return false;
      }
      const settings = settingsDoc.data() as MFASystemSettings;
      return settings.enabled;
    } catch (error) {
      console.error("Error checking MFA system status:", error);
      return false;
    }
  }

  /**
   * Check if MFA is required system-wide
   */
  async isMFARequired(): Promise<boolean> {
    try {
      const settingsDoc = await getDoc(doc(db, "system_settings", "mfa"));
      if (!settingsDoc.exists()) {
        return false;
      }
      const settings = settingsDoc.data() as MFASystemSettings;
      return settings.enabled && settings.required;
    } catch (error) {
      console.error("Error checking MFA requirement status:", error);
      return false;
    }
  }

  /**
   * Get MFA system settings
   */
  async getMFASystemSettings(): Promise<MFASystemSettings | null> {
    try {
      const settingsDoc = await getDoc(doc(db, "system_settings", "mfa"));
      if (!settingsDoc.exists()) {
        return null;
      }
      return settingsDoc.data() as MFASystemSettings;
    } catch (error) {
      console.error("Error getting MFA system settings:", error);
      return null;
    }
  }

  /**
   * Update MFA system settings (staff only)
   */
  async updateMFASystemSettings(
    settings: Partial<MFASystemSettings>,
    modifiedBy: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const currentSettings = await this.getMFASystemSettings();
      const updatedSettings: MFASystemSettings = {
        enabled: settings.enabled ?? currentSettings?.enabled ?? false,
        required: settings.required ?? currentSettings?.required ?? false,
        gracePeriodDays:
          settings.gracePeriodDays ?? currentSettings?.gracePeriodDays ?? 30,
        enabledBy: currentSettings?.enabledBy ?? modifiedBy,
        enabledAt: currentSettings?.enabledAt ?? new Date(),
        lastModifiedBy: modifiedBy,
        lastModifiedAt: new Date(),
      };

      await setDoc(doc(db, "system_settings", "mfa"), updatedSettings);
      return { success: true };
    } catch (error) {
      console.error("Error updating MFA system settings:", error);
      return { success: false, error: "Failed to update MFA settings" };
    }
  }

  /**
   * Generate TOTP secret using Firebase Auth
   */
  async generateTotpSecret(user: any): Promise<{
    secret: TotpSecret;
    qrCodeUrl: string;
    error?: string;
  } | null> {
    try {
      if (!user) {
        return null;
      }

      // Get multi-factor session
      const multiFactorSession = await multiFactor(user).getSession();

      // Generate TOTP secret
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(
        multiFactorSession
      );

      return {
        secret: totpSecret,
        qrCodeUrl: totpSecret.generateQrCodeUrl(),
      };
    } catch (error: any) {
      console.error("Error generating TOTP secret:", error);
      return {
        secret: null as any,
        qrCodeUrl: "",
        error: error.message || "Failed to generate TOTP secret",
      };
    }
  }

  /**
   * Enroll user in TOTP MFA
   */
  async enrollTotpMFA(
    user: any,
    totpSecret: TotpSecret,
    verificationCode: string,
    displayName?: string
  ): Promise<{ success: boolean; backupCodes?: string[]; error?: string }> {
    try {
      if (!user) {
        return { success: false, error: "User not authenticated" };
      }

      // Create TOTP credential
      const multiFactorAssertion =
        TotpMultiFactorGenerator.assertionForEnrollment(
          totpSecret,
          verificationCode
        );

      // Enroll the user
      await multiFactor(user).enroll(
        multiFactorAssertion,
        displayName || "Authenticator App"
      );

      // Generate backup codes
      const backupCodes = this.generateBackupCodes();

      // Save user MFA settings to Firestore
      const mfaSettings: MFAUserSettings = {
        userId: user.uid,
        hasTotp: true,
        totpEnrolledAt: new Date(),
        backupCodes,
        isRequired: false,
      };

      await setDoc(doc(db, "user_mfa_settings", user.uid), mfaSettings);

      return {
        success: true,
        backupCodes,
      };
    } catch (error: any) {
      console.error("Error enrolling TOTP MFA:", error);
      return {
        success: false,
        error: error.message || "Failed to enroll MFA",
      };
    }
  }

  /**
   * Check if user has MFA enrolled
   */
  async isUserMFAEnrolled(user: any): Promise<boolean> {
    try {
      if (!user) return false;

      const enrolledFactors = multiFactor(user).enrolledFactors;
      return enrolledFactors.length > 0;
    } catch (error) {
      console.error("Error checking user MFA enrollment:", error);
      return false;
    }
  }

  /**
   * Get user's enrolled MFA factors
   */
  getUserMFAFactors(user: any) {
    try {
      if (!user) return [];
      return multiFactor(user).enrolledFactors;
    } catch (error) {
      console.error("Error getting user MFA factors:", error);
      return [];
    }
  }

  /**
   * Unenroll from MFA
   */
  async unenrollMFA(
    user: any,
    factorUid: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      if (!user) {
        return { success: false, error: "User not authenticated" };
      }

      const enrolledFactors = multiFactor(user).enrolledFactors;
      const factorToDelete = enrolledFactors.find(
        (factor) => factor.uid === factorUid
      );

      if (!factorToDelete) {
        return { success: false, error: "MFA factor not found" };
      }

      await multiFactor(user).unenroll(factorToDelete);

      // Update Firestore settings
      await updateDoc(doc(db, "user_mfa_settings", user.uid), {
        hasTotp: false,
        lastUsed: new Date(),
      });

      return { success: true };
    } catch (error: any) {
      console.error("Error unenrolling MFA:", error);
      return {
        success: false,
        error: error.message || "Failed to unenroll MFA",
      };
    }
  }

  /**
   * Resolve MFA challenge during sign-in
   */
  async resolveMFAChallenge(
    resolver: MultiFactorResolver,
    verificationCode: string,
    factorIndex: number = 0
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const selectedHint = resolver.hints[factorIndex];

      if (selectedHint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        // Handle TOTP verification
        const multiFactorAssertion =
          TotpMultiFactorGenerator.assertionForSignIn(
            selectedHint.uid,
            verificationCode
          );

        await resolver.resolveSignIn(multiFactorAssertion);
        return { success: true };
      }

      return { success: false, error: "Unsupported MFA factor" };
    } catch (error: any) {
      console.error("Error resolving MFA challenge:", error);
      return {
        success: false,
        error: error.message || "Invalid verification code",
      };
    }
  }

  /**
   * Generate backup codes
   */
  generateBackupCodes(count: number = 10): string[] {
    const codes: string[] = [];
    for (let i = 0; i < count; i++) {
      // Generate 8-digit backup codes
      const code = Math.random().toString().slice(2, 10);
      codes.push(code);
    }
    return codes;
  }

  /**
   * Verify backup code
   */
  async verifyBackupCode(
    userId: string,
    code: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const settingsDoc = await getDoc(doc(db, "user_mfa_settings", userId));
      if (!settingsDoc.exists()) {
        return { success: false, error: "MFA settings not found" };
      }

      const settings = settingsDoc.data() as MFAUserSettings;
      if (!settings.backupCodes.includes(code)) {
        return { success: false, error: "Invalid backup code" };
      }

      // Remove used backup code
      const updatedBackupCodes = settings.backupCodes.filter(
        (bc) => bc !== code
      );
      await updateDoc(doc(db, "user_mfa_settings", userId), {
        backupCodes: updatedBackupCodes,
        lastUsed: new Date(),
      });

      return { success: true };
    } catch (error: any) {
      console.error("Error verifying backup code:", error);
      return { success: false, error: "Backup code verification failed" };
    }
  }

  /**
   * Regenerate backup codes
   */
  async regenerateBackupCodes(
    userId: string
  ): Promise<{ success: boolean; backupCodes?: string[]; error?: string }> {
    try {
      const newBackupCodes = this.generateBackupCodes();
      await updateDoc(doc(db, "user_mfa_settings", userId), {
        backupCodes: newBackupCodes,
      });

      return { success: true, backupCodes: newBackupCodes };
    } catch (error: any) {
      console.error("Error regenerating backup codes:", error);
      return { success: false, error: "Failed to regenerate backup codes" };
    }
  }

  /**
   * Get MFA statistics for admin dashboard
   */
  async getMFAStatistics(): Promise<{
    totalUsers: number;
    enabledUsers: number;
    enrollmentRate: number;
  }> {
    try {
      const mfaQuery = query(collection(db, "user_mfa_settings"));
      const mfaSnapshot = await getDocs(mfaQuery);
      const totalUsers = mfaSnapshot.size;

      let enabledUsers = 0;
      mfaSnapshot.forEach((doc) => {
        const data = doc.data() as MFAUserSettings;
        if (data.hasTotp) {
          enabledUsers++;
        }
      });

      const enrollmentRate =
        totalUsers > 0 ? (enabledUsers / totalUsers) * 100 : 0;

      return {
        totalUsers,
        enabledUsers,
        enrollmentRate: Math.round(enrollmentRate * 100) / 100,
      };
    } catch (error) {
      console.error("Error getting MFA statistics:", error);
      return {
        totalUsers: 0,
        enabledUsers: 0,
        enrollmentRate: 0,
      };
    }
  }

  /**
   * Get MFA resolver from auth error
   */
  getMFAResolver(authError: any): MultiFactorResolver | null {
    try {
      if (authError.code === "auth/multi-factor-auth-required") {
        return getMultiFactorResolver(auth, authError);
      }
      return null;
    } catch (error) {
      console.error("Error getting MFA resolver:", error);
      return null;
    }
  }
}

export const mfaService = MFAService.getInstance();
